import {AlertCircleIcon, ChevronDownIcon, ChevronUpIcon, Loader2Icon, RefreshCwIcon, SparklesIcon} from "lucide-react";
import {useCallback, useMemo, useState} from "react";
import Markdown from "react-markdown";

import {Badge} from "@components/ui/badge";
import {Button} from "@components/ui/button";
import {Card, CardContent, CardHeader, CardTitle} from "@components/ui/card";
import {refreshTabSummary, useVNextTabSummary} from "@hooks/use-vnext";

interface ExecutiveSummaryProps {
    workspaceId: string;
    scenarioId: string;
    tabKey: "ceo_summary" | "summary" | "pnl" | "cashflow" | "cashflow_summary" | "bs" | "bs_summary" | "pricing" | "health" | "ar" | "revenue" | "kpis" | "scenarios";
    // Period props — passed through from WorkspaceShell via each tab
    periodMonths?: number;
    currentMonth?: string;
    period?: string;
    selectedMonth?: string;
    customFrom?: string;
    customTo?: string;
    fyStartMonth?: number;
    fyEndMonth?: number;
}

function FreshnessBadge({status}: {status: string}) {
    switch (status) {
        case "fresh":
            return <Badge variant="outline" className="text-xs border-green-300 text-green-700">Fresh</Badge>;
        case "stale":
            return <Badge variant="outline" className="text-xs border-amber-300 text-amber-700">Stale</Badge>;
        case "generating":
            return (
                <Badge variant="outline" className="text-xs border-blue-300 text-blue-700">
                    <Loader2Icon className="size-3 mr-1 animate-spin" />
                    Generating
                </Badge>
            );
        case "failed":
            return <Badge variant="destructive" className="text-xs">Failed</Badge>;
        default:
            return null;
    }
}

/**
 * Compute how many months the selected period covers and the anchor month.
 * This mirrors the logic in CEOSummaryTab and WorkspaceShell.
 */
function usePeriodScope(props: ExecutiveSummaryProps) {
    const {period, currentMonth, selectedMonth, customFrom, customTo, fyStartMonth, fyEndMonth, periodMonths: explicitPeriodMonths} = props;

    const effectiveMonth = useMemo(() => {
        if (currentMonth) return currentMonth;
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    }, [currentMonth]);

    const periodMonths = useMemo(() => {
        // If caller already computed periodMonths, use it
        if (explicitPeriodMonths) return explicitPeriodMonths;
        if (!period) return undefined; // no period selected → don't filter

        const anchorDate = currentMonth
            ? new Date(currentMonth + "-01")
            : new Date();
        const anchorMonth = anchorDate.getMonth() + 1;
        const fyStart = fyEndMonth ? (fyEndMonth % 12) + 1 : (fyStartMonth ?? 1);

        switch (period) {
            case "MTD":
            case "MONTH":
                return 1;
            case "QTD": {
                const offset = ((anchorMonth - fyStart) % 12 + 12) % 12;
                return (offset % 3) + 1;
            }
            case "YTD": {
                const monthsIntoFY = anchorMonth >= fyStart
                    ? anchorMonth - fyStart + 1
                    : 12 - fyStart + anchorMonth + 1;
                return Math.max(1, monthsIntoFY);
            }
            case "FY":
                return 12;
            case "CUSTOM": {
                if (!customFrom || !customTo) return 12;
                const [fy, fm] = customFrom.split("-").map(Number);
                const [ty, tm] = customTo.split("-").map(Number);
                return Math.max(1, Math.min((ty - fy) * 12 + (tm - fm) + 1, 24));
            }
            default:
                return undefined;
        }
    }, [period, currentMonth, customFrom, customTo, fyStartMonth, fyEndMonth, explicitPeriodMonths]);

    // Build a human-readable period label for the prompt
    const periodLabel = useMemo(() => {
        if (!period) return undefined;
        switch (period) {
            case "MTD": return `MTD (${effectiveMonth})`;
            case "MONTH": return selectedMonth || effectiveMonth;
            case "QTD": return `QTD to ${effectiveMonth}`;
            case "YTD": return `YTD to ${effectiveMonth}`;
            case "FY": return "Full Year";
            case "CUSTOM": return customFrom && customTo ? `${customFrom} to ${customTo}` : undefined;
            default: return undefined;
        }
    }, [period, effectiveMonth, selectedMonth, customFrom, customTo]);

    return {effectiveMonth, periodMonths, periodLabel};
}

export function ExecutiveSummary(props: ExecutiveSummaryProps) {
    const {workspaceId, scenarioId, tabKey} = props;
    const {effectiveMonth, periodMonths, periodLabel} = usePeriodScope(props);
    const {summary, isLoading, refresh} = useVNextTabSummary(workspaceId, scenarioId, tabKey);
    const [refreshing, setRefreshing] = useState(false);
    const [genError, setGenError] = useState<string | null>(null);
    const [expanded, setExpanded] = useState(true);

    const handleRefresh = useCallback(async () => {
        setRefreshing(true);
        setGenError(null);
        try {
            await refreshTabSummary(workspaceId, scenarioId, tabKey, periodMonths, effectiveMonth);
            refresh();
        } catch (e) {
            const msg = e instanceof Error ? e.message : "Summary generation failed";
            setGenError(msg);
        } finally {
            setRefreshing(false);
        }
    }, [workspaceId, scenarioId, tabKey, periodMonths, effectiveMonth, refresh]);

    if (isLoading) return null;

    // Don't render the block at all if no summary exists yet
    if (!summary && !refreshing) {
        return (
            <Card className="border-dashed">
                <CardContent className="p-4 flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <SparklesIcon className="size-4" />
                            <span>Generate an AI executive summary for this tab{periodLabel ? ` (${periodLabel})` : ""}</span>
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleRefresh}
                            disabled={refreshing}
                        >
                            {refreshing ? (
                                <Loader2Icon className="size-3.5 mr-1.5 animate-spin" />
                            ) : (
                                <SparklesIcon className="size-3.5 mr-1.5" />
                            )}
                            Generate Summary
                        </Button>
                    </div>
                    {genError && (
                        <div className="flex items-center gap-1.5 text-sm text-red-600">
                            <AlertCircleIcon className="size-3.5 shrink-0" />
                            {genError}
                        </div>
                    )}
                </CardContent>
            </Card>
        );
    }

    const hasContent = !!(summary?.content_markdown || (refreshing && !summary?.content_markdown));

    return (
        <Card>
            <CardHeader className="py-2 px-4">
                <div className="flex items-center justify-between">
                    <CardTitle
                        className="flex items-center gap-2 text-sm font-medium cursor-pointer select-none"
                        onClick={() => hasContent && setExpanded(!expanded)}
                    >
                        <SparklesIcon className="size-4 text-[#209DB3]" />
                        Executive Summary
                        {periodLabel && (
                            <span className="text-xs font-normal text-muted-foreground">({periodLabel})</span>
                        )}
                        {summary && <FreshnessBadge status={summary.status} />}
                        {hasContent && (
                            expanded
                                ? <ChevronUpIcon className="size-3.5 text-muted-foreground" />
                                : <ChevronDownIcon className="size-3.5 text-muted-foreground" />
                        )}
                    </CardTitle>
                    <div className="flex items-center gap-2">
                        {summary?.generated_at && (
                            <span className="text-xs text-muted-foreground">
                                {new Date(summary.generated_at).toLocaleDateString(undefined, {
                                    month: "short",
                                    day: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                })}
                            </span>
                        )}
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2"
                            onClick={handleRefresh}
                            disabled={refreshing}
                        >
                            {refreshing ? (
                                <Loader2Icon className="size-3.5 animate-spin" />
                            ) : (
                                <RefreshCwIcon className="size-3.5" />
                            )}
                        </Button>
                    </div>
                </div>
            </CardHeader>
            {expanded && (
                <CardContent className="pt-0 px-4 pb-3">
                    {refreshing && !summary?.content_markdown ? (
                        <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
                            <Loader2Icon className="size-4 animate-spin" />
                            Generating executive summary...
                        </div>
                    ) : summary?.content_markdown ? (
                        <div
                            className="prose prose-sm max-w-none dark:prose-invert overflow-y-auto"
                            style={{resize: "vertical", minHeight: 80, height: 180, maxHeight: 600}}
                        >
                            <Markdown>{summary.content_markdown}</Markdown>
                        </div>
                    ) : null}
                </CardContent>
            )}
        </Card>
    );
}
